import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const BorrowManagementPage = () => {
  const navigate = useNavigate();
  const { approveBorrow, rejectBorrow, approveReturn, rejectReturn } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [borrows, setBorrows] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('borrow'); // 'borrow' or 'return'
  const [borrowFilterTab, setBorrowFilterTab] = useState('pending'); // pending, approved, overdue, all
  const [returnFilterTab, setReturnFilterTab] = useState('pending'); // pending, approved, rejected, all
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const loadBorrows = async () => {
      if (!currentTeam) {
        setLoading(false);
        return;
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        alert('คุณไม่มีสิทธิ์เข้าถึงหน้านี้');
        navigate('/nurse/equipment');
        return;
      }

      try {
        setLoading(true);
        
        // Load BORROW requests
        const borrowsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', currentTeam.id),
          where('type', '==', 'BORROW'),
          orderBy('createdAt', 'desc')
        );

        const borrowsSnapshot = await getDocs(borrowsQuery);
        const borrowsData = await Promise.all(
          borrowsSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const equipmentRef = doc(db, 'equipment', data.equipmentId);
            const equipmentSnap = await getDoc(equipmentRef);
            const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;
            const borrowerRef = doc(db, 'users', data.borrowedBy);
            const borrowerSnap = await getDoc(borrowerRef);
            const borrowerData = borrowerSnap.exists() ? borrowerSnap.data() : null;

            return {
              id: docSnapshot.id,
              ...data,
              equipment: equipmentData,
              borrower: borrowerData
            };
          })
        );

        // Load RETURN requests
        const returnsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', currentTeam.id),
          where('type', '==', 'RETURN'),
          orderBy('createdAt', 'desc')
        );

        const returnsSnapshot = await getDocs(returnsQuery);
        const returnsData = await Promise.all(
          returnsSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const equipmentRef = doc(db, 'equipment', data.equipmentId);
            const equipmentSnap = await getDoc(equipmentRef);
            const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;
            const returnerRef = doc(db, 'users', data.returnedBy);
            const returnerSnap = await getDoc(returnerRef);
            const returnerData = returnerSnap.exists() ? returnerSnap.data() : null;

            return {
              id: docSnapshot.id,
              ...data,
              equipment: equipmentData,
              returner: returnerData
            };
          })
        );

        // Separate borrows and returns
        setBorrows(borrowsData);
        setReturns(returnsData);
      } catch (err) {
        console.error('Error loading borrows:', err);
        // Fallback: query without orderBy
        try {
          // Load BORROW
          const fallbackBorrowsQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('type', '==', 'BORROW')
          );
          const borrowsSnapshot = await getDocs(fallbackBorrowsQuery);
          const borrowsData = await Promise.all(
            borrowsSnapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();
              const equipmentRef = doc(db, 'equipment', data.equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;
              const borrowerRef = doc(db, 'users', data.borrowedBy);
              const borrowerSnap = await getDoc(borrowerRef);
              const borrowerData = borrowerSnap.exists() ? borrowerSnap.data() : null;

              return {
                id: docSnapshot.id,
                ...data,
                equipment: equipmentData,
                borrower: borrowerData
              };
            })
          );

          // Load RETURN
          const fallbackReturnsQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('type', '==', 'RETURN')
          );
          const returnsSnapshot = await getDocs(fallbackReturnsQuery);
          const returnsData = await Promise.all(
            returnsSnapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();
              const equipmentRef = doc(db, 'equipment', data.equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;
              const returnerRef = doc(db, 'users', data.returnedBy);
              const returnerSnap = await getDoc(returnerRef);
              const returnerData = returnerSnap.exists() ? returnerSnap.data() : null;

              return {
                id: docSnapshot.id,
                ...data,
                equipment: equipmentData,
                returner: returnerData
              };
            })
          );

          // Separate and sort in memory
          borrowsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          returnsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          setBorrows(borrowsData);
          setReturns(returnsData);
        } catch (fallbackErr) {
          console.error('Error loading borrows (fallback):', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadBorrows();
  }, [currentTeam, navigate]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (expectedReturnAt) => {
    if (!expectedReturnAt) return null;
    const returnDate = expectedReturnAt.toDate ? expectedReturnAt.toDate() : new Date(expectedReturnAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    returnDate.setHours(0, 0, 0, 0);
    const diffTime = returnDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleApprove = async (borrowId) => {
    const borrowItem = borrows.find(b => b.id === borrowId);
    const isReturn = borrowItem?.type === 'RETURN';
    
    if (!window.confirm(isReturn ? 'ยืนยันการอนุมัติคำขอคืนอุปกรณ์?' : 'ยืนยันการอนุมัติคำขอยืมนี้หรือไม่?')) return;

    try {
      setIsProcessing(true);
      let result;
      if (isReturn) {
        result = await approveReturn(borrowId);
      } else {
        result = await approveBorrow(borrowId);
      }
      
      if (result.success) {
        alert(isReturn ? 'อนุมัติคำขอคืนสำเร็จ อุปกรณ์กลับเข้า stock แล้ว' : 'อนุมัติสำเร็จ');
        // Reload borrows
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    if (!rejectionReason.trim()) {
      alert('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    try {
      setIsProcessing(true);
      const borrowItem = borrows.find(b => b.id === rejectingId);
      const returnItem = returns.find(r => r.id === rejectingId);
      const isReturn = !!returnItem;
      
      let result;
      if (isReturn) {
        result = await rejectReturn(rejectingId, rejectionReason.trim());
      } else {
        result = await rejectBorrow(rejectingId, rejectionReason.trim());
      }
      
      if (result.success) {
        alert(isReturn ? 'ปฏิเสธคำขอคืนสำเร็จ' : 'ปฏิเสธสำเร็จ');
        setShowRejectModal(false);
        setRejectingId(null);
        setRejectionReason('');
        // Reload borrows
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter borrows
  const filteredBorrows = borrows.filter(borrow => {
    if (borrowFilterTab === 'all') return true;
    if (borrowFilterTab === 'pending') return borrow.status === 'PENDING';
    if (borrowFilterTab === 'approved') return borrow.status === 'APPROVED';
    if (borrowFilterTab === 'overdue') {
      const daysRemaining = getDaysRemaining(borrow.expectedReturnAt);
      return borrow.status === 'APPROVED' && daysRemaining !== null && daysRemaining < 0;
    }
    return true;
  });

  // Filter returns
  const filteredReturns = returns.filter(returnItem => {
    if (returnFilterTab === 'all') return true;
    if (returnFilterTab === 'pending') return returnItem.status === 'PENDING';
    if (returnFilterTab === 'approved') return returnItem.status === 'APPROVED';
    if (returnFilterTab === 'rejected') return returnItem.status === 'REJECTED';
    return true;
  });

  // Calculate statistics for borrows
  const borrowStats = {
    pending: borrows.filter(b => b.status === 'PENDING').length,
    approved: borrows.filter(b => b.status === 'APPROVED').length,
    overdue: borrows.filter(b => {
      const daysRemaining = getDaysRemaining(b.expectedReturnAt);
      return b.status === 'APPROVED' && daysRemaining !== null && daysRemaining < 0;
    }).length,
    total: borrows.length
  };

  // Calculate statistics for returns
  const returnStats = {
    pending: returns.filter(r => r.status === 'PENDING').length,
    approved: returns.filter(r => r.status === 'APPROVED').length,
    rejected: returns.filter(r => r.status === 'REJECTED').length,
    total: returns.length
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/nurse/equipment"
              className="text-gray-600 hover:text-gray-800"
            >
              ← กลับ
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">จัดการการยืมอุปกรณ์</h1>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-sm text-gray-600 mb-1">รออนุมัติยืม</p>
            <p className="text-2xl font-bold text-yellow-600">{borrowStats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-sm text-gray-600 mb-1">กำลังยืม</p>
            <p className="text-2xl font-bold text-green-600">{borrowStats.approved}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-sm text-gray-600 mb-1">คืนล่าช้า</p>
            <p className="text-2xl font-bold text-red-600">{borrowStats.overdue}</p>
          </div>
        </div>

        {/* Main Tabs: Borrow / Return */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex gap-2 border-b border-gray-200 mb-4">
            <button
              onClick={() => setMainTab('borrow')}
              className={`px-6 py-3 font-semibold text-lg border-b-2 transition ${
                mainTab === 'borrow'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📦 ยืม ({borrowStats.total})
            </button>
            <button
              onClick={() => setMainTab('return')}
              className={`px-6 py-3 font-semibold text-lg border-b-2 transition ${
                mainTab === 'return'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📥 คืน ({returnStats.total})
            </button>
          </div>

          {/* Filter Tabs - Borrow */}
          {mainTab === 'borrow' && (
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setBorrowFilterTab('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  borrowFilterTab === 'pending'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                รออนุมัติ ({borrowStats.pending})
              </button>
              <button
                onClick={() => setBorrowFilterTab('approved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  borrowFilterTab === 'approved'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                กำลังยืม ({borrowStats.approved})
              </button>
              <button
                onClick={() => setBorrowFilterTab('overdue')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  borrowFilterTab === 'overdue'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                คืนล่าช้า ({borrowStats.overdue})
              </button>
              <button
                onClick={() => setBorrowFilterTab('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  borrowFilterTab === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด ({borrowStats.total})
              </button>
            </div>
          )}

          {/* Filter Tabs - Return */}
          {mainTab === 'return' && (
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setReturnFilterTab('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  returnFilterTab === 'pending'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                รออนุมัติ ({returnStats.pending})
              </button>
              <button
                onClick={() => setReturnFilterTab('approved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  returnFilterTab === 'approved'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                อนุมัติแล้ว ({returnStats.approved})
              </button>
              <button
                onClick={() => setReturnFilterTab('rejected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  returnFilterTab === 'rejected'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ปฏิเสธแล้ว ({returnStats.rejected})
              </button>
              <button
                onClick={() => setReturnFilterTab('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  returnFilterTab === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด ({returnStats.total})
              </button>
            </div>
          )}
        </div>

        {/* Content List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : mainTab === 'borrow' ? (
          // Borrow List
          filteredBorrows.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่มีรายการยืม</h3>
              <p className="text-gray-600">ไม่มีรายการในหมวดหมู่นี้</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                📦 รายการยืม ({filteredBorrows.length})
              </h2>
              {filteredBorrows.map(borrow => {
              const daysRemaining = getDaysRemaining(borrow.expectedReturnAt);
              const isOverdue = daysRemaining !== null && daysRemaining < 0;

              return (
                <div
                  key={borrow.id}
                  className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition ${
                    isOverdue ? 'border-l-4 border-red-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {borrow.equipment && (
                          <>
                            <span className="text-2xl">🩺</span>
                            <div>
                              <h3 className="font-semibold text-gray-800">{borrow.equipment.name}</h3>
                              <p className="text-sm text-gray-600">{borrow.equipment.code}</p>
                            </div>
                          </>
                        )}
                        <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                          borrow.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          borrow.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          borrow.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {borrow.status === 'PENDING' ? '⏳ รออนุมัติ' :
                           borrow.status === 'APPROVED' ? '✅ อนุมัติแล้ว' :
                           borrow.status === 'REJECTED' ? '❌ ถูกปฏิเสธ' : borrow.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">👤 ผู้ยืม</p>
                          <p className="text-sm font-medium text-gray-800">
                            {borrow.borrowedByName || borrow.borrower?.displayName || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">📅 วันที่ยืม</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(borrow.borrowDate || borrow.createdAt)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">📅 วันที่คืนที่คาดหวัง</p>
                          <p className={`text-sm font-medium ${
                            isOverdue ? 'text-red-600' : daysRemaining !== null && daysRemaining <= 3 ? 'text-orange-600' : 'text-gray-800'
                          }`}>
                            {formatDate(borrow.expectedReturnAt)}
                            {daysRemaining !== null && (
                              <span className="ml-2">
                                ({isOverdue ? `ล่าช้า ${Math.abs(daysRemaining)} วัน` : `เหลืออีก ${daysRemaining} วัน`})
                              </span>
                            )}
                          </p>
                        </div>
                        {borrow.approvedAt && (
                          <div>
                            <p className="text-xs text-gray-500">✅ อนุมัติเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(borrow.approvedAt)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              โดย: {borrow.approvedByName || '-'}
                            </p>
                          </div>
                        )}
                        {borrow.rejectedAt && (
                          <div>
                            <p className="text-xs text-gray-500">❌ ปฏิเสธเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(borrow.rejectedAt)}
                            </p>
                            {borrow.rejectionReason && (
                              <p className="text-xs text-red-600 mt-1">
                                เหตุผล: {borrow.rejectionReason}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {borrow.notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">หมายเหตุ:</p>
                          <p className="text-sm text-gray-700">{borrow.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      {borrow.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleApprove(borrow.id)}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                          >
                            ✅ อนุมัติ
                          </button>
                          <button
                            onClick={() => {
                              setRejectingId(borrow.id);
                              setShowRejectModal(true);
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                          >
                            ❌ ปฏิเสธ
                          </button>
                        </>
                      )}
                      {borrow.equipment && (
                        <Link
                          to={`/nurse/equipment/${borrow.equipmentId}`}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                        >
                          ดูรายละเอียด
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )
        ) : (
          // Return List
          filteredReturns.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <div className="text-6xl mb-4">📥</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่มีรายการคืน</h3>
              <p className="text-gray-600">ไม่มีรายการในหมวดหมู่นี้</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                📥 รายการคืน ({filteredReturns.length})
              </h2>
              {filteredReturns.map(returnItem => {
                return (
                  <div
                    key={returnItem.id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {returnItem.equipment && (
                            <>
                              <span className="text-2xl">🩺</span>
                              <div>
                                <h3 className="font-semibold text-gray-800">{returnItem.equipment.name || returnItem.equipmentName}</h3>
                                <p className="text-sm text-gray-600">{returnItem.equipment.code || returnItem.equipmentCode}</p>
                              </div>
                            </>
                          )}
                          <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                            returnItem.status === 'PENDING' ? 'bg-blue-100 text-blue-800' :
                            returnItem.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                            returnItem.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {returnItem.status === 'PENDING' ? '📥 รออนุมัติคืน' :
                             returnItem.status === 'APPROVED' ? '✅ อนุมัติแล้ว' :
                             returnItem.status === 'REJECTED' ? '❌ ถูกปฏิเสธ' : returnItem.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <p className="text-xs text-gray-500">👤 ผู้คืน</p>
                            <p className="text-sm font-medium text-gray-800">
                              {returnItem.returnedByName || returnItem.returner?.displayName || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">📅 วันที่คืน</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDate(returnItem.actualReturnDate || returnItem.createdAt)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">📊 สภาพอุปกรณ์</p>
                            <p className="text-sm font-medium text-gray-800">
                              {returnItem.returnCondition === 'GOOD' ? '✅ ดี' :
                               returnItem.returnCondition === 'DAMAGED' ? '⚠️ เสียหาย' :
                               returnItem.returnCondition === 'NEEDS_REPAIR' ? '🔧 ต้องซ่อม' : '-'}
                            </p>
                          </div>
                          {returnItem.approvedAt && (
                            <div>
                              <p className="text-xs text-gray-500">✅ อนุมัติเมื่อ</p>
                              <p className="text-sm font-medium text-gray-800">
                                {formatDateTime(returnItem.approvedAt)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                โดย: {returnItem.approvedByName || '-'}
                              </p>
                            </div>
                          )}
                          {returnItem.rejectedAt && (
                            <div>
                              <p className="text-xs text-gray-500">❌ ปฏิเสธเมื่อ</p>
                              <p className="text-sm font-medium text-gray-800">
                                {formatDateTime(returnItem.rejectedAt)}
                              </p>
                              {returnItem.rejectionReason && (
                                <p className="text-xs text-red-600 mt-1">
                                  เหตุผล: {returnItem.rejectionReason}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {(returnItem.returnDescription || returnItem.notes) && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">รายละเอียด:</p>
                            <p className="text-sm text-gray-700">{returnItem.returnDescription || returnItem.notes}</p>
                          </div>
                        )}
                      </div>

                      <div className="ml-4 flex flex-col gap-2">
                        {returnItem.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => handleApprove(returnItem.id)}
                              disabled={isProcessing}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                            >
                              ✅ อนุมัติคืน
                            </button>
                            <button
                              onClick={() => {
                                setRejectingId(returnItem.id);
                                setShowRejectModal(true);
                              }}
                              disabled={isProcessing}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                            >
                              ❌ ปฏิเสธ
                            </button>
                          </>
                        )}
                        {returnItem.equipment && (
                          <Link
                            to={`/nurse/equipment/${returnItem.equipmentId}`}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                          >
                            ดูรายละเอียด
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {(borrows.find(b => b.id === rejectingId) || returns.find(r => r.id === rejectingId))?.type === 'RETURN' ? 'ปฏิเสธคำขอคืน' : 'ปฏิเสธคำขอยืม'}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เหตุผลที่ปฏิเสธ <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="กรุณาระบุเหตุผล..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingId(null);
                  setRejectionReason('');
                }}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium disabled:opacity-50"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleReject}
                disabled={isProcessing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                {isProcessing ? 'กำลังส่ง...' : 'ยืนยัน'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BorrowManagementPage;

